import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ConfirmDialogComponent, ConfirmDialogData } from '../../../../shared/confirm-dialog/confirm-dialog.component';
import {
  LISTEN_TYPEN,
  ListenStruktur,
  ListenStrukturVorlage,
  ListenTyp,
  kennzeichnungsSpalte,
  pruefeStruktur
} from '../../../../../Models/messstelle.model';
import { InbetriebnahmeService } from '../../../../../Services/inbetriebnahme.service';
import { AuthService } from '../../../../../api-authorization/authorize.service';


/** Arbeitsstand einer Vorlage im Bearbeitungsbereich. */
interface VorlagenEntwurf {
  /** Null = neue Vorlage. */
  id: number | null;
  name: string;
  beschreibung: string;
  listenTyp: ListenTyp;
  struktur: ListenStruktur;
  gueltig: boolean;
}


/**
 * Verwaltung der Strukturvorlagen: ansehen, bearbeiten, duplizieren, löschen.
 *
 * Eine Vorlage enthält nur die Spaltenstruktur – keine Messstellen, keine
 * Prüfergebnisse. Listen, die aus einer Vorlage entstanden sind, tragen eine
 * eigene Kopie; weder Ändern noch Löschen einer Vorlage erreicht sie.
 *
 * Rechte wie beim Erstellen von Listen: Lesen darf jeder, Schreiben nur mit
 * Schreibrecht (kein Montage-/Vorarbeiter-Konto).
 */
@Component({
  selector: 'app-struktur-vorlagen',
  templateUrl: './struktur-vorlagen.component.html',
  styleUrls: ['./struktur-vorlagen.component.css']
})
export class StrukturVorlagenComponent implements OnInit {

  vorlagen: ListenStrukturVorlage[] = [];
  isLoading = true;
  loadError = '';
  suche = '';

  /** Aufgeklappte Vorschau je Vorlage. */
  offen = new Set<number>();

  entwurf: VorlagenEntwurf | null = null;
  speichert = false;
  entwurfFehler = '';

  loeschtId: number | null = null;
  dupliziertId: number | null = null;

  readonly listenTypen = LISTEN_TYPEN.filter(t => t.wert !== 'Unbekannt');


  constructor(
    private service: InbetriebnahmeService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService
  ) { }


  get canEdit(): boolean {
    return this.authService.canEdit();
  }


  ngOnInit(): void {
    this.lade();
  }


  lade(): void {
    this.isLoading = true;
    this.loadError = '';

    this.service.getStrukturVorlagen().subscribe({
      next: vorlagen => {
        this.vorlagen = vorlagen;
        this.isLoading = false;
      },
      error: () => {
        this.loadError = 'Die Vorlagen konnten nicht geladen werden.';
        this.isLoading = false;
      }
    });
  }


  get gefiltert(): ListenStrukturVorlage[] {
    const s = this.suche.trim().toLowerCase();

    if (!s) {
      return this.vorlagen;
    }

    return this.vorlagen.filter(v =>
      v.name.toLowerCase().includes(s)
      || (v.beschreibung ?? '').toLowerCase().includes(s)
      || v.struktur.spalten.some(sp => sp.name.toLowerCase().includes(s)));
  }


  kennzeichnungVon(v: ListenStrukturVorlage): string {
    return kennzeichnungsSpalte(v.struktur)?.name ?? v.kennzeichnungLabel ?? 'Kennzeichnung';
  }


  listenTypText(typ: ListenTyp): string {
    return LISTEN_TYPEN.find(t => t.wert === typ)?.text ?? typ;
  }


  umschalten(v: ListenStrukturVorlage): void {
    if (this.offen.has(v.id)) {
      this.offen.delete(v.id);
    } else {
      this.offen.add(v.id);
    }
  }


  trackByVorlage(_index: number, v: ListenStrukturVorlage): number {
    return v.id;
  }


  // ── Bearbeiten / Neu ──

  neu(): void {
    if (!this.canEdit) {
      return;
    }

    this.entwurf = {
      id: null,
      name: '',
      beschreibung: '',
      listenTyp: 'Messstellenliste',
      struktur: { spalten: [] },
      gueltig: false
    };

    this.entwurfFehler = '';
  }


  bearbeiten(v: ListenStrukturVorlage): void {
    if (!this.canEdit) {
      return;
    }

    this.entwurf = {
      id: v.id,
      name: v.name,
      beschreibung: v.beschreibung ?? '',
      listenTyp: v.listenTyp === 'Unbekannt' ? 'Messstellenliste' : v.listenTyp,
      // Kopie – der Editor arbeitet nie auf der angezeigten Vorlage.
      struktur: { spalten: v.struktur.spalten.map(s => ({ ...s })) },
      gueltig: pruefeStruktur(v.struktur).length === 0
    };

    this.entwurfFehler = '';
  }


  entwurfStruktur(struktur: ListenStruktur): void {
    if (this.entwurf) {
      this.entwurf.struktur = struktur;
      this.entwurfFehler = '';
    }
  }


  entwurfGueltig(gueltig: boolean): void {
    if (this.entwurf) {
      this.entwurf.gueltig = gueltig;
    }
  }


  get kannSpeichern(): boolean {
    return !!this.entwurf
      && !this.speichert
      && this.entwurf.name.trim().length > 0
      && this.entwurf.gueltig
      && pruefeStruktur(this.entwurf.struktur).length === 0;
  }


  speichern(): void {

    if (!this.entwurf || !this.kannSpeichern) {
      return;
    }

    const e = this.entwurf;

    this.speichert = true;
    this.entwurfFehler = '';

    const request = {
      name: e.name.trim(),
      beschreibung: e.beschreibung.trim() || null,
      listenTyp: e.listenTyp,
      struktur: e.struktur
    };

    const aufruf = e.id == null
      ? this.service.createStrukturVorlage(request)
      : this.service.updateStrukturVorlage(e.id, request);

    aufruf.subscribe({
      next: gespeichert => {
        this.speichert = false;
        this.entwurf = null;

        const index = this.vorlagen.findIndex(v => v.id === gespeichert.id);

        if (index >= 0) {
          this.vorlagen[index] = gespeichert;
        } else {
          this.vorlagen = [...this.vorlagen, gespeichert]
            .sort((a, b) => a.name.localeCompare(b.name, 'de'));
        }

        this.snackBar.open(
          e.id == null ? 'Vorlage angelegt.' : 'Vorlage gespeichert. Bestehende Listen bleiben unverändert.',
          'OK',
          { duration: 3500 }
        );
      },
      error: err => {
        this.speichert = false;
        this.entwurfFehler = err?.error?.message ?? 'Die Vorlage konnte nicht gespeichert werden.';
      }
    });
  }


  abbrechen(): void {
    if (!this.speichert) {
      this.entwurf = null;
      this.entwurfFehler = '';
    }
  }


  // ── Duplizieren / Löschen ──

  duplizieren(v: ListenStrukturVorlage): void {

    if (!this.canEdit || this.dupliziertId != null) {
      return;
    }

    this.dupliziertId = v.id;

    this.service.dupliziereStrukturVorlage(v.id).subscribe({
      next: kopie => {
        this.dupliziertId = null;
        this.vorlagen = [...this.vorlagen, kopie].sort((a, b) => a.name.localeCompare(b.name, 'de'));
        this.snackBar.open(`Vorlage „${kopie.name}“ angelegt.`, 'OK', { duration: 3000 });
      },
      error: err => {
        this.dupliziertId = null;
        this.snackBar.open(err?.error?.message ?? 'Duplizieren fehlgeschlagen.', 'OK', { duration: 4000 });
      }
    });
  }


  loeschen(v: ListenStrukturVorlage): void {

    if (!this.canEdit || this.loeschtId != null) {
      return;
    }

    const data: ConfirmDialogData = {
      title: 'Vorlage löschen',
      message: `Die Vorlage „${v.name}“ wird dauerhaft gelöscht.`,
      details: [
        `${v.spaltenCount} Spalte${v.spaltenCount === 1 ? '' : 'n'}, Kennzeichnung: ${this.kennzeichnungVon(v)}`
      ],
      warning: 'Bereits erstellte Inbetriebnahmelisten bleiben vollständig erhalten – sie besitzen eine eigene Kopie der Struktur.',
      confirmText: 'Löschen'
    };

    this.dialog
      .open(ConfirmDialogComponent, { data, maxWidth: '520px' })
      .afterClosed()
      .subscribe(bestaetigt => {

        if (!bestaetigt) {
          return;
        }

        this.loeschtId = v.id;

        this.service.deleteStrukturVorlage(v.id).subscribe({
          next: () => {
            this.loeschtId = null;
            this.vorlagen = this.vorlagen.filter(x => x.id !== v.id);
            this.offen.delete(v.id);

            if (this.entwurf?.id === v.id) {
              this.entwurf = null;
            }

            this.snackBar.open('Vorlage gelöscht.', 'OK', { duration: 3000 });
          },
          error: err => {
            this.loeschtId = null;
            this.snackBar.open(err?.error?.message ?? 'Löschen fehlgeschlagen.', 'OK', { duration: 4000 });
          }
        });
      });
  }


  // ── Navigation ──

  zurueck(): void {
    this.router.navigate(['/wartung/inbetriebnahme']);
  }


  neueListe(v?: ListenStrukturVorlage): void {
    this.router.navigate(['/wartung/inbetriebnahme/neu'], v ? { queryParams: { vorlageId: v.id } } : undefined);
  }
}
